import { useEffect, useMemo, useState } from 'react'
import { Icon, type IconName } from '../components/ui/Icon'
import type { GovernancePosition, GovernanceStageKey } from './governance-api'
import { governanceStageLabel } from '../lib/display-labels'
import '../styles/governance.css'

type StageGuidance = {
  purpose: string
  helpsYou: string[]
  involves: string
  completion: string
}

const stages: { key: GovernanceStageKey; label: string; description: string }[] = [
  { key: 'register', label: 'Register', description: 'Identity, purpose and accountability' },
  { key: 'assess', label: 'Assess', description: 'Formal assessment and evidence' },
  { key: 'control', label: 'Control', description: 'Required safeguards and evidence' },
  { key: 'approve', label: 'Approve', description: 'Human governance decisions' },
  { key: 'monitor', label: 'Monitor', description: 'Conditions, changes and incidents' },
  { key: 'review', label: 'Review', description: 'Periodic review and reassessment' },
]

const guidance: Record<GovernanceStageKey, StageGuidance> = {
  register: {
    purpose: 'This is the accountable record of how the organisation is using AI. It establishes what the AI use is, why it exists, who owns it and which product or supplier is involved.',
    helpsYou: ['Keep a clear inventory of AI use across the organisation.', 'Give the business owner a single place to maintain factual information.', 'Provide the starting point for every later governance decision.'],
    involves: 'The business owner supplies the operational context. A Governance Coordinator can help maintain the record, but should not guess the business facts.',
    completion: 'The record contains enough identity, ownership, purpose and product information for Swell to assess the AI use properly.',
  },
  assess: {
    purpose: 'The formal assessment gathers the facts that determine the governance approach. It helps the organisation understand the AI use, its context, impacts and the evidence that supports those conclusions.',
    helpsYou: ['Identify whether safeguards, approval or follow-up are needed.', 'Record the reasoning behind the governance position.', 'Route factual questions to the people best placed to answer them.'],
    involves: 'The business owner provides day-to-day context. Relevant specialists may contribute where the use involves privacy, security, people, legal, safety or other specialist considerations.',
    completion: 'The required assessment questions and contributor confirmations have been completed. Swell then determines which later lifecycle stages apply.',
  },
  control: {
    purpose: 'Controls are the practical safeguards that make an AI use safe and governable in its real operating environment. They can include policies, access restrictions, human checks, training or evidence of operation.',
    helpsYou: ['Translate assessment findings into practical actions.', 'Keep required safeguards visible and owned.', 'Retain evidence that important safeguards are actually operating.'],
    involves: 'The business owner coordinates the work. Control owners and relevant specialists provide the action or evidence for the safeguards they are responsible for.',
    completion: 'All required controls are complete, any overdue work is resolved, and the necessary evidence has been recorded.',
  },
  approve: {
    purpose: 'Approval records an explicit human governance decision. It makes clear whether the AI use may proceed, any conditions attached to it, and who made the decision.',
    helpsYou: ['Ensure decisions are made by an authorised person, not automatically by the system.', 'Record the decision and its conditions for future reference.', 'Make approval or reapproval needs visible before the AI use changes.'],
    involves: 'The business owner prepares the request. An authorised approver makes the decision; Swell never appoints a universal organisation-wide AI approver.',
    completion: 'The authorised decision is recorded, including any conditions. If the AI use later changes materially, Swell can require the decision to be reconsidered.',
  },
  monitor: {
    purpose: 'Monitoring keeps the governance record connected to real life after the AI use is operating. It tracks conditions, material changes and incidents that could change its governance position.',
    helpsYou: ['Keep obligations and commitments from being forgotten.', 'Bring material changes or incidents back into view promptly.', 'Show whether the current approval and assessment are still reliable.'],
    involves: 'The business owner reports meaningful changes and incidents. Owners of conditions or follow-up actions keep their work up to date.',
    completion: 'There are no outstanding monitoring obligations, or any open items have a clear owner and plan. Monitoring continues while the AI use remains in service.',
  },
  review: {
    purpose: 'Review is the periodic check that the registered information, assessment and governance decisions remain current. It prevents an old decision being treated as valid after the business context has moved on.',
    helpsYou: ['Confirm the AI use still operates as recorded.', 'Identify changes that require reassessment or reapproval.', 'Keep a clear history of ongoing governance, rather than a one-off exercise.'],
    involves: 'The business owner confirms the current facts. Governance staff coordinate the review cycle and involve specialists or approvers when the review identifies a material change.',
    completion: 'The review is recorded, the next review date is set where applicable, and any required follow-up has been assigned.',
  },
}

function label(value: string) {
  const labels: Record<string, string> = {
    pending_assessment: 'Pending assessment',
    not_applicable: 'Not applicable',
    not_required: 'Not required',
    not_scheduled: 'Not scheduled',
    needs_attention: 'Needs attention',
    not_started: 'Not started',
    in_progress: 'In progress',
    awaiting_approval: 'Awaiting approval',
    not_ready: 'Not ready',
    changes_requested: 'Changes requested',
    reassessment_required: 'Reassessment required',
    reassessment_in_progress: 'Reassessment in progress',
    reapproval_required: 'Reapproval required',
    completed_actions_required: 'Review actions required',
    completed_reassessment_required: 'Reassessment required',
    completed_no_change: 'Completed',
  }
  return labels[value] ?? value.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())
}

function tone(state: string) {
  if (['complete', 'approved', 'current', 'completed_no_change', 'not_applicable', 'not_required'].includes(state)) return 'complete'
  if (['overdue', 'reassessment_required', 'reapproval_required', 'rejected', 'needs_attention', 'changes_requested'].includes(state)) return 'attention'
  if (['in_progress', 'reassessment_in_progress', 'active', 'draft', 'submitted', 'evaluated', 'awaiting_approval', 'due', 'completed_actions_required', 'completed_reassessment_required'].includes(state)) return 'current'
  return 'neutral'
}

function statusIcon(state: string): IconName {
  if (['complete', 'approved', 'current', 'completed_no_change'].includes(state)) return 'check'
  if (['overdue', 'reassessment_required', 'reapproval_required', 'rejected', 'needs_attention', 'changes_requested'].includes(state)) return 'alert'
  if (state === 'pending_assessment') return 'clock'
  if (state === 'not_applicable' || state === 'not_required') return 'info'
  if (state === 'not_scheduled') return 'clock'
  return 'clock'
}

function nextAction(key: GovernanceStageKey, state: string) {
  if (state === 'pending_assessment') return 'Complete the formal assessment before Swell determines whether this later stage applies.'
  if (state === 'complete' || state === 'approved' || state === 'current' || state === 'completed_no_change' || state === 'not_applicable' || state === 'not_required') return 'No immediate action is required.'
  if (state === 'not_scheduled' && key === 'review') return 'Set an appropriate review date when this AI use is ready to enter its ongoing review cycle.'
  const actions: Record<GovernanceStageKey, string> = {
    register: 'Complete the missing identity, ownership or product information so the record can be relied upon.',
    assess: state === 'reassessment_required' ? 'Complete the new formal assessment cycle using the current facts about this AI use.' : 'Start or continue the formal assessment. Answer only the questions you can confirm; Swell will identify when specialist input is needed.',
    control: state === 'overdue' ? 'Address the overdue controls and record the evidence that they are operating.' : 'Complete the required controls and record the supporting evidence.',
    approve: state === 'reapproval_required' ? 'Prepare and route a new approval request because the previous decision needs to be reconsidered.' : state === 'awaiting_approval' ? 'An authorised human decision is pending. The approver can approve, approve with conditions, or request changes.' : 'Resolve the approval readiness or routing requirements shown in the assessment.',
    monitor: state === 'overdue' ? 'Address the overdue monitoring obligations and update the record with the outcome.' : 'Review open conditions, material changes or incidents and confirm who owns the next step.',
    review: state === 'overdue' ? 'Complete the overdue periodic review using the current facts about this AI use.' : 'Complete the current review obligation and record whether anything has changed.',
  }
  return actions[key]
}

function statusMeaning(key: GovernanceStageKey, state: string) {
  if (state === 'pending_assessment') return 'This stage has not been decided yet because the formal assessment is still needed. It is not a problem by itself: Swell waits for the assessment rather than assuming a requirement.'
  if (state === 'complete' || state === 'approved' || state === 'current' || state === 'completed_no_change') return 'The work currently required for this stage is complete. Keep the underlying facts current so Swell can identify when the position needs to change.'
  if (state === 'not_applicable') return 'The completed assessment has determined that this stage does not apply to this AI use at present. If the use changes materially, Swell may bring the stage back into scope.'
  if (state === 'not_required') return 'The current governance rules do not require this stage for this AI use. This is a recorded outcome, not an omitted task.'
  if (state === 'not_scheduled') return key === 'review' ? 'No periodic review date has been set yet. The AI use can still be governed, but a review schedule should be established when appropriate.' : 'This stage has not been scheduled yet.'
  if (state === 'needs_attention') return 'Important information is missing or no longer reliable. Resolve the item shown below so the governance position can be trusted.'
  if (state === 'not_started') return 'This stage has not started. Review the recommended next action to see whether it is required now.'
  if (state === 'in_progress') return 'Work is underway. Swell will update the lifecycle position as the required facts, evidence or decisions are recorded.'
  if (state === 'reassessment_in_progress') return 'The reassessment has started and is using the current facts about this AI use. Continue the existing assessment rather than starting another one.'
  if (state === 'awaiting_approval') return 'The request is ready for an authorised human decision. Swell records the decision but does not approve AI uses automatically.'
  if (state === 'reassessment_required') return 'A change means the earlier assessment can no longer be treated as current. Reassess the AI use using its current operating context.'
  if (state === 'reapproval_required') return 'A change means the earlier approval should be reconsidered by an authorised person before reliance continues.'
  if (state === 'overdue') return 'A required activity is past its due point. Address it promptly so the governance record reflects the AI use as it operates today.'
  if (state === 'changes_requested') return 'More information or a change is needed before this stage can be completed. Review the returned work and update the relevant record.'
  return `This stage is currently marked ${label(state).toLowerCase()}. Use the explanation and next action below to understand what that means for this AI use.`
}

function missingLabel(value: string) {
  const labels: Record<string, string> = {
    ai_use_name: 'AI use name',
    business_owner: 'business owner',
    business_area: 'business area',
    purpose: 'business purpose',
    product: 'AI product or supplier',
    product_id: 'AI product or supplier',
    supplier: 'supplier',
    operational_state: 'operating status',
  }
  return labels[value] ?? value.replaceAll('_', ' ')
}

function defaultStage(position: GovernancePosition): GovernanceStageKey {
  const priority: GovernanceStageKey[] = ['register', 'assess', 'control', 'approve', 'monitor', 'review']
  return priority.find((key) => ['needs_attention', 'overdue', 'reassessment_required', 'reassessment_in_progress', 'reapproval_required', 'rejected', 'changes_requested', 'in_progress', 'awaiting_approval', 'due'].includes(position.stages[key].state)) ?? 'register'
}

function attentionCount(key: GovernanceStageKey, stage: GovernancePosition['stages'][GovernanceStageKey]) {
  if (key === 'register') return stage.missing?.length ?? 0
  if (key === 'control') return stage.blockingCount ?? stage.overdueCount ?? 0
  if (key === 'monitor') return (stage.openConditions ?? 0) + (stage.openMaterialChanges ?? 0) + (stage.openIncidents ?? 0)
  if (key === 'review' && ['due', 'overdue', 'reassessment_required'].includes(stage.state)) return 1
  return ['needs_attention', 'overdue', 'reassessment_required', 'reapproval_required', 'rejected', 'changes_requested'].includes(stage.state) ? 1 : 0
}

const registerStageMeta = { key: 'register' as GovernanceStageKey, label: 'Register', description: 'Identity, purpose and accountability' }

export function GovernancePath({ position, loading = false }: { position?: GovernancePosition; loading?: boolean }) {
  const initial = useMemo(() => position ? defaultStage(position) : 'register', [position])
  const [selected, setSelected] = useState<GovernanceStageKey>(initial)

  useEffect(() => setSelected(initial), [initial])

  if (loading) return <section className="panel governance-workspace"><p>Loading governance position…</p></section>
  if (!position) return null

  const selectedMeta = stages.find((stage) => stage.key === selected) ?? registerStageMeta
  const selectedStage = position.stages[selected]
  const selectedGuidance = guidance[selected]
  const selectedAttentionCount = attentionCount(selected, selectedStage)

  return (
    <section className="panel governance-workspace governance-workspace-single governance-lifecycle-primary" aria-labelledby="governance-path-heading">
      <div className="section-heading governance-workspace-heading">
        <div>
          <p className="eyebrow">Governance lifecycle</p>
          <h2 id="governance-path-heading">Governance lifecycle</h2>
          <p>See what is complete, what needs attention and what happens next.</p>
        </div>
      </div>

      <nav className="governance-path governance-path-navigator" aria-label="AI governance lifecycle">
        {stages.map(({ key, label: stageLabel }) => {
          const state = position.stages[key].state
          const isSelected = selected === key
          return (
            <button key={key} type="button" className={`governance-path-stage governance-path-stage-${key} ${tone(state)}${isSelected ? ' selected' : ''}`} aria-current={isSelected ? 'step' : undefined} onClick={() => setSelected(key)}>
              <span className="governance-stage-status-icon" aria-hidden="true"><Icon name={statusIcon(state)} size={18} /></span>
              <span className="governance-stage-label">{stageLabel}</span>
              <strong>{governanceStageLabel(key, state)}</strong>
            </button>
          )
        })}
      </nav>

      <article id={`governance-${selected}`} className={`governance-stage-detail governance-stage-detail-compact ${tone(selectedStage.state)}`} aria-live="polite">
        <div className="governance-stage-detail-heading">
          <div>
            <p className="eyebrow">{selectedMeta.label}</p>
            <h3>{selectedMeta.label} — {governanceStageLabel(selected, selectedStage.state).toLowerCase()}</h3>
          </div>
          <span>{selectedMeta.description}</span>
        </div>

        <div className="governance-stage-next-action">
          <span>What happens next</span>
          <p>{nextAction(selected, selectedStage.state)}</p>
        </div>

        {selectedAttentionCount > 0 ? <p className="governance-stage-attention-count"><strong>{selectedAttentionCount} {selectedAttentionCount === 1 ? 'item needs' : 'items need'} attention</strong></p> : null}

        {selected === 'register' && selectedStage.missing?.length ? <p className="governance-stage-callout"><strong>Information still needed:</strong> {selectedStage.missing.map(missingLabel).join(', ')}.</p> : null}
        {selected === 'control' && typeof selectedStage.blockingCount === 'number' ? <p className="governance-stage-meta"><strong>Current control position:</strong> {selectedStage.blockingCount} blocking item{selectedStage.blockingCount === 1 ? '' : 's'} · {selectedStage.overdueCount ?? 0} overdue</p> : null}
        {selected === 'monitor' ? <p className="governance-stage-meta"><strong>Current monitoring position:</strong> {selectedStage.openConditions ?? 0} open condition{selectedStage.openConditions === 1 ? '' : 's'} · {selectedStage.openMaterialChanges ?? 0} material change{selectedStage.openMaterialChanges === 1 ? '' : 's'} · {selectedStage.openIncidents ?? 0} incident{selectedStage.openIncidents === 1 ? '' : 's'}</p> : null}
        {selected === 'review' && selectedStage.nextReviewDate ? <p className="governance-stage-meta"><strong>Next review:</strong> {new Date(selectedStage.nextReviewDate).toLocaleDateString('en-AU', { dateStyle: 'medium' })}</p> : null}

        <details className="governance-stage-explainer">
          <summary>What happens at this stage</summary>
          <div className="governance-stage-explainer-content">
            <p>{selectedGuidance.purpose}</p>
            <section>
              <h4>What this status means</h4>
              <p>{statusMeaning(selected, selectedStage.state)}</p>
            </section>
            <section>
              <h4>How this stage helps</h4>
              <ul>{selectedGuidance.helpsYou.map((item) => <li key={item}>{item}</li>)}</ul>
            </section>
            <section>
              <h4>Who is involved</h4>
              <p>{selectedGuidance.involves}</p>
            </section>
            <section>
              <h4>When it is complete</h4>
              <p>{selectedGuidance.completion}</p>
            </section>
          </div>
        </details>
      </article>
    </section>
  )
}
